/**
 * API back-office : gestion des comptes parents (liste, bloquer, supprimer, stats).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface EnfantLie {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  relation: 'pere' | 'mere' | 'tuteur' | 'autre';
  relation_label: string;
}

export interface ParentItem {
  id: number;
  nom: string;
  prenoms: string;
  email: string;
  telephone: string;
  canal_prefere: 'email' | 'sms';
  date_creation: string | null;
  derniere_activite: string | null;
  nb_visites: number;
  nb_enfants: number;
  enfants: EnfantLie[];
  bloque: boolean;
  bloque_raison: string;
  bloque_date: string | null;
  valide_inscription: boolean;
  date_validation: string | null;
}

export interface ParentsListResponse {
  results: ParentItem[];
  total: number;
  kpis: {
    total: number;
    valides: number;
    bloques: number;
    en_attente_validation: number;
    en_ligne: number;
    actifs_24h: number;
    actifs_7j: number;
  };
}

export interface ParentsStatsResponse {
  activite_30j: { date: string; parents_actifs: number }[];
  inscriptions_30j: { date: string; inscriptions: number }[];
}

export const parentsApi = createApi({
  reducerPath: 'parentsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Parents', 'ParentsStats'],
  endpoints: (builder) => ({
    listerParents: builder.query<ParentsListResponse, {
      statut?: 'tous' | 'valides' | 'bloques' | 'en_attente' | 'en_ligne';
      search?: string;
    } | void>({
      query: (params) => ({
        url: '/portail-parent/admin/parents/',
        params: params ?? undefined,
      }),
      providesTags: ['Parents'],
    }),

    parentsStats: builder.query<ParentsStatsResponse, void>({
      query: () => '/portail-parent/admin/parents/stats/',
      providesTags: ['ParentsStats'],
    }),

    bloquerParent: builder.mutation<{ success: boolean; bloque: boolean }, {
      id: number;
      action: 'bloquer' | 'debloquer';
      raison?: string;
    }>({
      query: ({ id, ...body }) => ({
        url: `/portail-parent/admin/parents/${id}/bloquer/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Parents'],
    }),

    supprimerParent: builder.mutation<{ success: boolean }, {
      id: number;
      raison?: string;
    }>({
      query: ({ id, raison }) => ({
        url: `/portail-parent/admin/parents/${id}/`,
        method: 'DELETE',
        body: { raison },
      }),
      invalidatesTags: ['Parents', 'ParentsStats'],
    }),
  }),
});

export const {
  useListerParentsQuery,
  useParentsStatsQuery,
  useBloquerParentMutation,
  useSupprimerParentMutation,
} = parentsApi;
